/**
 * The Bilt Cash planner: its inputs, and all of its arithmetic.
 *
 * The plan runs here rather than on the server so it redraws as the balance
 * and milestone fields are edited. The server's job is to keep rent encrypted
 * at rest and hand it only to this signed-in page.
 *
 * It plans the Flexible Bilt Cash option, kept past the card anniversary by
 * opting out: 4% back on spend, which unlocks points on rent.
 */

import { authedFetch } from "./api";
import { orThrow } from "./http";
import { formatExact, formatWhole } from "./money";

/**
 * Bilt's rules as read off its own pages on 2026-10-27: Bilt Cash Terms (updated
 * 2026-10-27), the Card 2.0 Program Overview, and Rewards T&C section 7. The
 * anniversary is this card's, from Bilt's concierge.
 */
export const RULES = {
  /** 4% Bilt Cash on everyday spend. Housing payments earn none. */
  cashRate: 0.04,
  /** $3 of Bilt Cash unlocks 100 points on rent, up to 1 point per $1. */
  unlockRate: 0.03,
  /** Palladium everyday spend. Bonus categories earn more, so a $50 can land sooner. */
  pointsPerDollar: 2,
  /** $50 per 25,000 qualifying points in a calendar year. Rent points count. */
  milestonePoints: 25_000,
  milestoneCash: 50,
  /** Each award lasts through the end of the 12th month after the one it was earned in; oldest spent first. */
  expiryMonths: 12,
  /** Palladium's annual Bilt Cash credit posts on the card anniversary, every year from this one. */
  anniversary: "2027-02-23",
  anniversaryGrant: 200,
  /** Card 2.0 began 2026-02-07, so nothing on hand can expire before this month ends. */
  earliestBalanceExpiry: "2027-02",
} as const;

/** Under a cent is float noise, never a real shortfall. */
export const EPS = 0.005;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function monthName(month: number): string {
  return MONTH_NAMES[month] ?? "";
}

export interface ExpiringLot {
  /** YYYY-MM: it expires at the end of this month. */
  month: string;
  amount: number;
}

export interface BiltCashDto {
  /** YYYY-MM-DD in Pacific time, from the server. */
  today: string;
  /** The balance, and the same balance by the month each part of it expires. */
  cash: { amount: number; as_of: string; expiring: ExpiringLot[] } | null;
  points_to_next: { points: number; as_of: string } | null;
  rent: { state: "ok" | "unset" | "locked"; amount: number | null; message: string | null };
}

export async function fetchBiltCash(): Promise<BiltCashDto> {
  const res = await authedFetch("/api/bilt-cash");
  return (await orThrow(res, "Could not load Bilt Cash")) as BiltCashDto;
}

export async function saveRent(amount: number): Promise<void> {
  const res = await authedFetch("/api/bilt-cash/rent", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount }),
  });
  await orThrow(res, "Could not save rent");
}

export interface PlanInputs {
  today: string;
  cash: number;
  pointsToNext: number;
  rent: number;
  /** When each part of the balance expires, as last read off Bilt; without it the expiry is guessed. */
  expiring?: ExpiringLot[];
}

export interface RentMonth {
  year: number;
  /** 0 = January. */
  month: number;
}

export interface MonthRow extends RentMonth {
  spend: number;
  cashOnRentDay: number;
  unlockCost: number;
  unlocked: number;
  short: number;
  bonuses: number;
  /** Bilt Cash that reached its expiry unspent since the previous rent. */
  expired: number;
  /** The anniversary credit, when it lands before this rent. */
  granted: number;
  cashAfter: number;
}

export interface Path {
  rows: MonthRow[];
  /** Bilt Cash that reached its expiry unspent over the plan. */
  expired: number;
}

/** Card spend posted before a month's rent, given the cash that rent day will see. */
export type SpendRule = (cash: number, unlockCost: number, month: RentMonth) => number;

export const earnNothing = (): number => 0;

export const earnJustEnough = (cash: number, unlockCost: number): number => {
  const gap = unlockCost - cash;
  return gap > EPS ? gap / RULES.cashRate : 0;
};

export const spendEachMonth =
  (perMonth: number): SpendRule =>
  () =>
    perMonth;

/**
 * Every rent month from the next one through December: this year's until July 1,
 * next year's from then on, so next year is planned only in this year's second half.
 * Rent is due on the 1st, so after the 1st this month's is already paid.
 */
export function rentMonths(today: string): RentMonth[] {
  const thisYear = Number(today.slice(0, 4));
  const lastYear = Number(today.slice(5, 7)) >= 7 ? thisYear + 1 : thisYear;
  let year = thisYear;
  let month = Number(today.slice(5, 7)) - 1;
  if (Number(today.slice(8, 10)) > 1) month += 1;
  const out: RentMonth[] = [];
  for (;;) {
    if (month > 11) {
      month = 0;
      year += 1;
    }
    if (year > lastYear) break;
    out.push({ year, month });
    month += 1;
  }
  return out;
}

/** Months as one counter, so "12 months later" is + 12. */
function monthIndex(year: number, month: number): number {
  return year * 12 + month;
}

function indexOf(date: string): number {
  return monthIndex(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1);
}

/** The first rent on or after a date: rent is due on the 1st. */
function firstRentFrom(date: string): number {
  return indexOf(date) + (Number(date.slice(8, 10)) > 1 ? 1 : 0);
}

/**
 * The month the balance on hand is treated as expiring at the end of, when
 * Bilt's breakdown is missing: the earliest it could, but never before the next rent.
 */
export function balanceExpiry(today: string): RentMonth {
  const i = Math.max(indexOf(RULES.earliestBalanceExpiry), firstRentFrom(today));
  return { year: Math.floor(i / 12), month: i % 12 };
}

interface Lot {
  amount: number;
  /** The last month index it can be spent in. */
  through: number;
}

interface State {
  lots: Lot[];
  /** Qualifying points still to earn before the next $50. */
  left: number;
  /** The calendar year `left` counts toward. */
  year: number;
  expired: number;
}

function balance(state: State): number {
  return state.lots.reduce((sum, l) => sum + l.amount, 0);
}

function addCash(state: State, amount: number, through: number): void {
  if (amount <= 0) return;
  state.lots.push({ amount, through });
  state.lots.sort((a, b) => a.through - b.through);
}

/** Spends oldest-expiring first. */
function spendCash(state: State, amount: number): void {
  let rest = amount;
  for (const lot of state.lots) {
    const take = Math.min(lot.amount, rest);
    lot.amount -= take;
    rest -= take;
  }
  state.lots = state.lots.filter((l) => l.amount > 1e-9);
}

/** Drops what expired at the end of a month before `month`, and returns how much. */
function expireBefore(state: State, month: number): number {
  let gone = 0;
  for (const lot of state.lots) if (lot.through < month) gone += lot.amount;
  state.lots = state.lots.filter((l) => l.through >= month);
  state.expired += gone;
  return gone;
}

/** Adds points earned in `year` and returns how many $50 milestones they crossed. */
function earnPoints(state: State, points: number, year: number, through: number): number {
  if (year > state.year) {
    state.year = year;
    state.left = RULES.milestonePoints;
  }
  state.left -= points;
  let crossed = 0;
  while (state.left <= 0) {
    crossed += 1;
    state.left += RULES.milestonePoints;
  }
  addCash(state, crossed * RULES.milestoneCash, through);
  return crossed;
}

/**
 * The anniversary credit landing before rent month `i`, if any: one each year
 * from the first, and none that landed by `today`, since the balance already holds it.
 */
function grantFor(i: number, today: string): number {
  const first = firstRentFrom(RULES.anniversary);
  if (i < first || (i - first) % 12 !== 0) return 0;
  const landed = `${Number(RULES.anniversary.slice(0, 4)) + (i - first) / 12}${RULES.anniversary.slice(4)}`;
  return landed <= today ? 0 : RULES.anniversaryGrant;
}

/**
 * The balance on hand as dated awards. With Bilt's breakdown, the balance
 * figure still decides how much: spent since comes off the oldest awards, and
 * earned since is a new award; a month already past is gone. Without one, it
 * is all treated as expiring at `guess`.
 */
function startLots(inputs: PlanInputs, guess: number): Lot[] {
  const cash = Math.max(0, inputs.cash);
  if (!inputs.expiring?.length) return [{ amount: cash, through: guess }];
  const now = indexOf(inputs.today);
  const state: State = { lots: [], left: 0, year: 0, expired: 0 };
  for (const lot of inputs.expiring) addCash(state, lot.amount, indexOf(`${lot.month}-01`));
  // Reconciled before past months drop, so a stale reading's lapsed month is
  // gone rather than counted again as newly earned.
  const gap = cash - balance(state);
  if (gap < -EPS) spendCash(state, -gap);
  else if (gap > EPS) addCash(state, gap, now + RULES.expiryMonths);
  return state.lots.filter((l) => l.through >= now);
}

export function simulate(inputs: PlanInputs, spendRule: SpendRule): Path {
  const unlockCost = inputs.rent * RULES.unlockRate;
  const from = Number(inputs.today.slice(0, 4));
  const first = firstRentFrom(RULES.anniversary);
  const start = balanceExpiry(inputs.today);
  const state: State = {
    lots: [],
    left: Math.min(RULES.milestonePoints, Math.max(1, inputs.pointsToNext)),
    year: from,
    expired: 0,
  };
  for (const lot of startLots(inputs, monthIndex(start.year, start.month))) addCash(state, lot.amount, lot.through);

  const rows: MonthRow[] = [];
  for (const m of rentMonths(inputs.today)) {
    const i = monthIndex(m.year, m.month);
    // The spend before rent posts the month before: January's in December.
    const posted = i - 1;
    const granted = grantFor(i, inputs.today);
    // What this rent day would see with nothing earned, which is what the rule sizes against.
    const seen =
      state.lots.filter((l) => l.through >= i).reduce((s, l) => s + l.amount, 0) + granted;
    const spend = Math.max(0, spendRule(seen, unlockCost, m));

    addCash(state, spend * RULES.cashRate, posted + RULES.expiryMonths);
    let bonuses = earnPoints(
      state,
      spend * RULES.pointsPerDollar,
      Math.max(Math.floor(posted / 12), from),
      posted + RULES.expiryMonths,
    );
    const expired = expireBefore(state, i);
    // The credit is earned in the anniversary month, so it expires a year after that.
    if (granted) addCash(state, granted, indexOf(RULES.anniversary) + (i - first) + RULES.expiryMonths);

    const cashOnRentDay = balance(state);
    const unlocked = Math.min(unlockCost, cashOnRentDay);
    spendCash(state, unlocked);
    // Points unlocked on rent are rent points, and those count toward a $50 too.
    bonuses += earnPoints(state, unlocked / RULES.unlockRate, Math.max(m.year, from), i + RULES.expiryMonths);
    rows.push({
      ...m,
      spend,
      cashOnRentDay,
      unlockCost,
      unlocked,
      short: unlockCost - unlocked,
      bonuses,
      expired,
      granted,
      cashAfter: balance(state),
    });
  }
  return { rows, expired: state.expired };
}

/** Whole dollars, rounded up: a minimum rounded down would fall short. */
export function atLeast(n: number): number {
  return Math.max(0, Math.ceil(n - EPS));
}

/** Smallest x in [0, hi] that passes a test that only turns true as x rises. */
function leastPassing(ok: (x: number) => boolean, hi: number): number {
  if (ok(0)) return 0;
  let lo = 0;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (ok(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

/**
 * Least whole-dollar amount that passes. Rounding the bisected threshold is not
 * enough: a cent below it a $50 lands one phase later, after rent has already
 * come up short, so step up until it really passes.
 */
function leastWholeDollars(ok: (x: number) => boolean, hi: number): number {
  let x = atLeast(leastPassing(ok, hi));
  while (!ok(x) && x < hi) x += 1;
  return x;
}

/** The least steady monthly spend that unlocks every rent in the plan. */
export function breakEven(inputs: PlanInputs): number {
  // Earns a full unlock every month, so it always covers.
  const ceiling = (inputs.rent * RULES.unlockRate) / RULES.cashRate + 1;
  const covers = (x: number) =>
    simulate(inputs, spendEachMonth(x)).rows.every((r) => r.short <= EPS);
  return leastWholeDollars(covers, ceiling);
}

export interface Plan {
  /** The least spend before each rent that covers it, month by month. */
  justEnough: Path;
  /** One steady amount a month that covers every rent. */
  breakEven: number;
}

export function buildPlan(inputs: PlanInputs): Plan {
  return { justEnough: simulate(inputs, earnJustEnough), breakEven: breakEven(inputs) };
}

export type Tone = "accent" | "good" | "warn" | "bad" | "plain";

export interface Tile {
  label: string;
  value: string;
  hint: string;
  tone: Tone;
}

/** "Jun 2027" from a YYYY-MM. */
export function monthLabel(month: string): string {
  return `${monthName(Number(month.slice(5, 7)) - 1)} ${month.slice(0, 4)}`;
}

export interface YearAverage {
  year: number;
  /** The months whose spend posts in that year: "Oct–Dec 2026", or "all of 2027". */
  span: string;
  /** The least spend each month, rounded up as the table shows it, averaged. */
  average: number;
}

/**
 * The average monthly spend per calendar year the spend posts in. A rent's spend
 * posts the month before it, so January's counts toward December; never before
 * today's year, since on Jan 1 that rent's spend is already behind us.
 */
export function yearAverages(rows: MonthRow[], today: string): YearAverage[] {
  const from = Number(today.slice(0, 4));
  const years = new Map<number, { spends: number[]; months: number[] }>();
  for (const r of rows) {
    const posted = monthIndex(r.year, r.month) - 1;
    const year = Math.max(Math.floor(posted / 12), from);
    const entry = years.get(year) ?? { spends: [], months: [] };
    entry.spends.push(atLeast(r.spend));
    entry.months.push(posted % 12);
    years.set(year, entry);
  }
  return [...years].map(([year, { spends, months }]) => {
    const first = months[0] ?? 0;
    const last = months.at(-1) ?? first;
    const span = spends.length === 12
      ? `all of ${year}`
      : first === last
        ? `${monthName(first)} ${year}`
        : `${monthName(first)}–${monthName(last)} ${year}`;
    return { year, span, average: Math.ceil(spends.reduce((s, x) => s + x, 0) / spends.length) };
  });
}

export function summaryTiles(plan: Plan, inputs: PlanInputs): Tile[] {
  const tiles: Tile[] = [];
  const { rows } = plan.justEnough;
  const first = rows[0];
  const last = rows.at(-1);
  if (first) {
    const label = `Before ${monthName(first.month)} rent`;
    tiles.push(
      first.spend < EPS
        ? {
            label,
            value: "Covered",
            hint: `${formatExact(first.cashOnRentDay)} on hand for a ${formatExact(first.unlockCost)} unlock`,
            tone: "good",
          }
        : {
            label,
            value: `Spend ${formatWhole(atLeast(first.spend))}`,
            hint: "on the card, at least 5 days before rent posts",
            tone: "warn",
          },
    );
  }
  if (last) {
    const total = rows.reduce((s, r) => s + atLeast(r.spend), 0);
    tiles.push({
      label: "Steady spend a month",
      value: formatWhole(plan.breakEven),
      hint: `covers every rent through ${monthName(last.month)} ${last.year}; month by month it takes ${formatWhole(total)} in all`,
      tone: "accent",
    });
  }
  const years = yearAverages(rows, inputs.today);
  const [firstYear, secondYear] = years;
  if (firstYear) {
    tiles.push({
      label: "Average a month",
      value:
        secondYear && secondYear.average !== firstYear.average
          ? `${formatWhole(firstYear.average)}, then ${formatWhole(secondYear.average)}`
          : formatWhole(firstYear.average),
      hint: `${years.map((y) => `${y.span} at ${formatWhole(y.average)}`).join(", then ")}, at the least spend each month`,
      tone: "accent",
    });
  }
  const next = inputs.expiring?.find((l) => l.amount > EPS && l.month >= inputs.today.slice(0, 7));
  tiles.push(
    plan.justEnough.expired > EPS
      ? {
          label: "Expires unspent",
          value: formatExact(plan.justEnough.expired),
          hint: "at the least spend shown, before rent can use it; spend it on another redemption",
          tone: "bad",
        }
      : {
          label: "Expires unspent",
          value: "Nothing",
          hint: next
            ? `rent uses it first: the oldest, ${formatExact(next.amount)}, is good through ${monthLabel(next.month)}`
            : "rent uses it before any of it expires",
          tone: "good",
        },
  );
  const grant = rows.find((r) => r.granted > 0);
  if (grant) {
    tiles.push({
      label: "Anniversary credit",
      value: `+${formatWhole(grant.granted)}`,
      hint: `lands before ${monthName(grant.month)} ${grant.year} rent, if you keep this option by opting out`,
      tone: "plain",
    });
  }
  return tiles;
}

export interface ChartScale {
  max: number;
  ticks: number[];
}

/** One y scale for the line, reference line and tick labels alike. */
export function chartScale(values: number[]): ChartScale {
  const peak = Math.max(1, ...values);
  const step = peak > 1600 ? 500 : peak > 800 ? 200 : peak > 400 ? 100 : 50;
  const max = Math.ceil((peak * 1.08) / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  return { max, ticks };
}
